import {useEffect, useCallback} from 'react';

import algoliasearch from 'algoliasearch/lite';

import {logWithSentry} from 'libs/sentry/sentry';

import {QUERIES_LIMIT, REGULAR_PRODUCT_LIMIT} from '../constants/quickSearch';
import {AlgoliaAutocompleteDTOType} from '../types';
import {normalizeDirectAlgoliaResponse} from './normalize';

const appId = 'SU25FBOU6E';
const apiKey = 'a9e1cd7cac5d9ea088ce88137dd92dcc';

const DEBOUNCE_VALUE_MS = 100;
export const searchClient = algoliasearch(appId, apiKey);

type PropType = {
    query: string;
    searchingCategory: string;
    setQuickSearchState: Function;
};

/**
 * Debounces provided function
 * @param {Function} fn - function to debounce
 * @param {number} timeout - debounce timeout in ms
 * @returns {Function} - debounced function
 */
export const debounce = <P extends unknown[]>(fn: (...args: P) => void, timeout: number) => {
    let timer: ReturnType<typeof setTimeout>;
    return (...args: P) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            fn(...args);
        }, timeout);
        return timer;
    };
};

/**
 * Requests autocomplete results with algolia client
 * @param {string} query - request query
 * @param {string} searchingCategory - search key
 * @returns {Promise<AlgoliaAutocompleteDTOType>} - promise resolving into autocomplete data
 */
export const getAutocompleteResults = (
    query: string,
    searchingCategory = '',
): Promise<AlgoliaAutocompleteDTOType> => {
    const searchParams =
        searchingCategory === 'ContactLenses' ? {filters: "category: 'Contact Lenses'"} : {};

    const hitsLimit = REGULAR_PRODUCT_LIMIT;

    // @ts-ignore: incompatibility with return algolia readonly generic type
    return searchClient.search([
        {
            indexName: 'PopularSearches',
            query,
            params: {
                hitsPerPage: QUERIES_LIMIT,
                attributesToRetrieve: ['display_value', 'link'],
                clickAnalytics: true,
                ...searchParams,
            },
        },
        {
            indexName: 'GlassesUSA',
            query,
            params: {
                hitsPerPage: hitsLimit,
                attributesToRetrieve: ['title', 'link', 'image_link', 'product_sku'],
                ...searchParams,
            },
        },
    ]);
};

/**
 * Returns autocomplete results got from Algolia cilent
 * @param {PropType} - request query parameters
 * @returns {void} - formatted response
 */
export const useAutocompleteResults = ({
    query,
    searchingCategory,
    setQuickSearchState,
}: PropType): void => {
    const handleResults = async (queryStr: string, siteKeyStr: string) => {
        if (queryStr === '') {
            setQuickSearchState(currState => ({
                query: currState.query,
                isLoading: false,
                searchResults: {},
            }));
            return;
        }
        try {
            const responseData = await getAutocompleteResults(queryStr, siteKeyStr);
            setQuickSearchState(currState => ({
                query: currState.query,
                isLoading: false,
                searchResults: normalizeDirectAlgoliaResponse(responseData),
            }));
        } catch (error) {
            logWithSentry(error, 'getQuickSearchResults error');
            setQuickSearchState(currState => ({
                query: currState.query,
                isLoading: false,
                searchResults: {},
            }));
        }
    };

    const handleResultsWithDebounce = useCallback(debounce(handleResults, DEBOUNCE_VALUE_MS), []);

    useEffect(() => {
        const debounceTimer = handleResultsWithDebounce(query, searchingCategory);
        return () => clearTimeout(debounceTimer);
    }, [query, searchingCategory]);
};
